import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import {
  Alan, Anahtar, DilAlan, DilAnahtari, KaydetCubugu, SayfaBaslik, Sonuc, YayinDugmesi, useCikisUyarisi, useSiteDurum, useTaslak,
  type Dil, type IkiDil,
} from './ortak';

type Genel = {
  firmaAdi: string; tanitim: IkiDil; telefon: string; whatsapp: string; whatsappAcik: boolean; eposta: string;
  adres: string[]; haritaUrl: string; saatler: IkiDil; showroomNotu: IkiDil; sosyal: { ad: string; adres: string }[];
};

/** İletişim telefonunun varsayılanı (sunucudaki SITE_TELEFON ile aynı). Telefon boş bırakılamaz. */
const SITE_TELEFON = '+90 530 772 83 79';
const TELEFON = /^\+?[0-9][0-9 ()-]{6,24}$/;

/** Kullanıcı Google'ın verdiği <iframe …> kodunu yapıştırırsa içinden adresi alırız; siteye ham HTML gitmez. */
const haritaAdresi = (ham: string) => /<iframe[^>]*\ssrc=["']([^"']+)["']/i.exec(ham)?.[1] ?? ham;

/** Ayar sayfası düzeni: solda başlık + tek satır açıklama, sağda kart. */
function AyarBolumu({ baslik, aciklama, children }: { baslik: string; aciklama: string; children: React.ReactNode }) {
  return (
    <section className="ccd-site-ayar">
      <div className="ccd-site-ayar-sol">
        <h2 className="ccd-site-kart-baslik">{baslik}</h2>
        <div className="ccd-meta">{aciklama}</div>
      </div>
      <div className="ccd-card ccd-stack">{children}</div>
    </section>
  );
}

/** Site › Ayarlar — firma, iletişim, adres ve harita, çalışma saatleri, sosyal medya. */
export default function SiteAyarlar() {
  const { durum, yenile } = useSiteDurum();
  const { f, setF, yukle, kirli, vazgec } = useTaslak<Genel>();
  const [dil, setDil] = useState<Dil>('tr');
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  useCikisUyarisi(kirli);

  useEffect(() => {
    api<{ veri: Genel }>('/api/site/icerik/genel').then((r) => yukle(r.veri)).catch((e) => setHata((e as Error).message));
  }, [yukle]);

  if (!f) return <div className="ccd-stack"><SayfaBaslik baslik="Site ayarları" /><Sonuc bilgi={null} hata={hata} />{!hata && <div className="ccd-inline-note">Yükleniyor…</div>}</div>;
  const al = <K extends keyof Genel>(k: K, v: Genel[K]) => { setBilgi(null); setF({ ...f, [k]: v }); };
  const adres = [...f.adres, '', ''].slice(0, Math.max(2, f.adres.length));
  const telefonHata = !f.telefon.trim() ? 'Telefon boş bırakılamaz.' : !TELEFON.test(f.telefon.trim()) ? 'Telefon yalnız rakam, boşluk ve + içerebilir.' : null;
  const sosyalTurleri = durum?.secenekler.sosyal ?? ['Instagram'];

  async function kaydet() {
    setBilgi(null);
    if (telefonHata) { setHata(telefonHata); document.getElementById('sg-tel')?.focus(); return; }
    setHata(null); setMesgul(true);
    try {
      const r = await api<{ veri: Genel }>('/api/site/icerik/genel', {
        method: 'PUT', body: JSON.stringify({ ...f, adres: f!.adres.map((s) => s.trim()).filter(Boolean), sosyal: f!.sosyal.filter((s) => s.adres.trim()) }),
      });
      yukle(r.veri); setBilgi('Taslak kaydedildi. Sitede görünmesi için yayınlayın.');
      await yenile();
    } catch (err) { setHata(err instanceof Error ? err.message : 'Kaydedilemedi'); }
    finally { setMesgul(false); }
  }

  return (
    <div className="ccd-stack">
      <KaydetCubugu acik={kirli} mesgul={mesgul} kaydet={() => void kaydet()} vazgec={() => { setHata(null); vazgec(); }} />
      <SayfaBaslik baslik="Site ayarları" durum={durum}>
        <DilAnahtari dil={dil} degis={setDil} />
        <YayinDugmesi durum={durum} yenile={() => void yenile()} birincil={false} engel={kirli ? 'Önce değişiklikleri kaydedin' : null} />
        <button type="button" className="ccd-btn ccd-btn-primary" disabled={mesgul || !kirli} onClick={() => void kaydet()}>{mesgul ? 'Kaydediliyor…' : 'Kaydet'}</button>
      </SayfaBaslik>
      <Sonuc bilgi={bilgi} hata={hata} />

      <AyarBolumu baslik="Firma" aciklama="Sayfa başlıklarında ve arama motoru künyesinde görünür.">
        <Alan id="sg-ad" etiket="Firma adı" ipucu="Logodaki yazı tasarımın parçasıdır; buradan değişmez.">
          <input id="sg-ad" className="ccd-input" maxLength={80} value={f.firmaAdi} onChange={(e) => al('firmaAdi', e.target.value)} />
        </Alan>
        <DilAlan id="sg-tanitim" etiket="Kısa tanıtım" dil={dil} deger={f.tanitim} degis={(v) => al('tanitim', v)} azami={300} cokSatir sayac />
      </AyarBolumu>

      <AyarBolumu baslik="İletişim" aciklama="Alt bilgi, iletişim sayfası ve WhatsApp düğmesi.">
        <Alan id="sg-tel" etiket={<>Telefon <span className="ccd-required">*</span></>} hata={telefonHata}
              ipucu="Sitenin her yerinde bu numara görünür; WhatsApp bağlantısı da bundan türetilir.">
          <div className="ccd-site-yanyana">
            <input id="sg-tel" className={`ccd-input${telefonHata ? ' ccd-input-invalid' : ''}`} required inputMode="tel" maxLength={30} value={f.telefon}
                   aria-invalid={Boolean(telefonHata)} onChange={(e) => al('telefon', e.target.value)} />
            {f.telefon.trim() !== SITE_TELEFON && <button type="button" className="ccd-btn" title={SITE_TELEFON} onClick={() => al('telefon', SITE_TELEFON)}>Varsayılan</button>}
          </div>
        </Alan>
        <Alan id="sg-eposta" etiket="E-posta">
          <input id="sg-eposta" className="ccd-input" type="email" maxLength={120} value={f.eposta} onChange={(e) => al('eposta', e.target.value)} />
        </Alan>
        <div className="ccd-divider" />
        <Anahtar acik={f.whatsappAcik} degis={(v) => al('whatsappAcik', v)} etiket="Sitede “Üreticiye Sor” (WhatsApp) düğmesi görünsün" />
        <details className="ccd-tema-ileri" open={Boolean(f.whatsapp)}>
          <summary>WhatsApp için ayrı numara</summary>
          <Alan id="sg-wa" etiket="WhatsApp numarası" ipucu="Boşsa telefon numarası kullanılır.">
            <input id="sg-wa" className="ccd-input" inputMode="tel" maxLength={30} value={f.whatsapp} onChange={(e) => al('whatsapp', e.target.value)} />
          </Alan>
        </details>
      </AyarBolumu>

      <AyarBolumu baslik="Adres ve harita" aciklama="İletişim sayfasındaki adres ve Google haritası.">
        <div className="ccd-field">
          <label className="ccd-field-label" htmlFor="sg-adres-0">Adres</label>
          <div className="ccd-stack-sm">
            {adres.map((satir, i) => (
              <input key={i} id={`sg-adres-${i}`} className="ccd-input" maxLength={160} value={satir} aria-label={`Adres ${i + 1}. satır`}
                     placeholder={i === 0 ? 'Mahalle, sokak, kapı no' : 'İlçe, il, ülke'}
                     onChange={(e) => { const y = [...adres]; y[i] = e.target.value; al('adres', y); }} />
            ))}
          </div>
        </div>
        <Alan id="sg-harita" etiket="Harita bağlantısı" ipucu="Google Haritalar › Paylaş › “Harita yerleştir” kodunu yapıştırın; adres içinden alınır.">
          <input id="sg-harita" className="ccd-input ccd-mono" maxLength={1500} placeholder="https://www.google.com/maps/embed?pb=…" value={f.haritaUrl}
                 onChange={(e) => al('haritaUrl', haritaAdresi(e.target.value).trim())} />
        </Alan>
      </AyarBolumu>

      <AyarBolumu baslik="Çalışma saatleri" aciklama="İletişim sayfasında; boş bırakılan satır görünmez.">
        <DilAlan id="sg-saat" etiket="Çalışma saatleri" dil={dil} deger={f.saatler} degis={(v) => al('saatler', v)} azami={160}
                 ipucu="Örn. Hafta içi 09:00 – 18:00 · Cumartesi 09:00 – 14:00" />
        <DilAlan id="sg-showroom" etiket="Showroom notu" dil={dil} deger={f.showroomNotu} degis={(v) => al('showroomNotu', v)} azami={200}
                 ipucu="Saatlerin altındaki küçük not (örn. randevu ile ziyaret)." />
      </AyarBolumu>

      <AyarBolumu baslik="Sosyal medya" aciklama="Alt bilgideki hesap bağlantıları.">
        {f.sosyal.length === 0 && <div className="ccd-meta">Hesap eklenmedi; sitede bugünkü bağlantılar kalır.</div>}
        {f.sosyal.map((s, i) => (
          <div className="ccd-site-sosyal" key={i}>
            <select className="ccd-select" aria-label="Sosyal medya türü" value={s.ad}
                    onChange={(e) => al('sosyal', f.sosyal.map((x, n) => (n === i ? { ...x, ad: e.target.value } : x)))}>
              {(sosyalTurleri.includes(s.ad) ? sosyalTurleri : [s.ad, ...sosyalTurleri]).map((ad) => <option key={ad} value={ad}>{ad}</option>)}
            </select>
            <input className="ccd-input" aria-label={`${s.ad} adresi`} placeholder="https://…" maxLength={300} value={s.adres}
                   onChange={(e) => al('sosyal', f.sosyal.map((x, n) => (n === i ? { ...x, adres: e.target.value } : x)))} />
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" aria-label={`${s.ad} hesabını kaldır`} onClick={() => al('sosyal', f.sosyal.filter((_, n) => n !== i))}>Kaldır</button>
          </div>
        ))}
        <div>
          <button type="button" className="ccd-btn ccd-btn-sm" disabled={f.sosyal.length >= 8}
                  onClick={() => al('sosyal', [...f.sosyal, { ad: sosyalTurleri.find((s) => !f.sosyal.some((x) => x.ad === s)) ?? 'Instagram', adres: '' }])}>Hesap ekle</button>
        </div>
        <div className="ccd-field-hint">Ana sayfadaki Instagram karoları ilk Instagram hesabına bağlanır.</div>
      </AyarBolumu>
    </div>
  );
}
